import type { CapabilityConfig } from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';
import {
  isDelegatableEntitlement,
  type FronteggExtensionsPermissionKeys,
} from '../../../entitlements';

export const id = 'extension-token';

export const Events = undefined;
export const Actions = undefined;

/**
 * Config for the `extension-token` capability.
 *
 * `entitlements` declares which permission keys this app wants narrowed
 * into the DTA-issued extension token's `scope.entitlements` claim. At
 * mint time the API intersects the declaration with the calling user's
 * Frontegg permissions and the central delegatable-entitlements
 * allowlist; only keys present in all three reach the token.
 *
 * Each value must appear in the delegatable-entitlements allowlist —
 * extensions cannot request keys outside that set. Manifests containing
 * unknown or non-delegatable keys fail validation.
 */
export type Config = CapabilityConfig & {
  type: typeof id;
  entitlements?: FronteggExtensionsPermissionKeys[];
};

const VALID_CONFIG_KEYS = ['type', 'entitlements'] as const;

export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  const { entitlements } = config as Config;
  if (entitlements !== undefined) {
    if (
      !Array.isArray(entitlements) ||
      !entitlements.every(
        (e) => typeof e === 'string' && isDelegatableEntitlement(e),
      )
    ) {
      return false;
    }
  }

  return true;
}
